import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["row", "name", "onChange", "id"],
    _excluded2 = ["onBlur"],
    _excluded3 = ["name", "tableHeaders", "values", "customNumCols", "$css", "className", "collapseError", "errorText"];
import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { focusStyles, hoverChild, isKeyboard, positionAbsolute, TRANSITION_DEFAULT, highContrast, highContrastGrayColor, highContrastOutline, gridColSpan } from '../../../../styling';
import { TYPOGRAPHY_CLASSNAMES, COLORS } from '../../../../themes';
import { dotToDash, toAlphanumericDashString } from '../../../../utils';
import StyledContainer from '../../../Styled/StyledContainer';
import { useExtendedField } from '../../../../hooks';
import { Div } from '../../../Styled';
import { InputError } from '../Error';
var Wrapper = /*#__PURE__*/styled(StyledContainer).withConfig({
  displayName: "TableRadioInput__Wrapper",
  componentId: "sc-voepmi-0"
})(["width:100%;border-spacing:0;thead,tbody{display:block;width:100%;tr{display:grid;", ";th,td{text-align:left;padding-right:10px;&:first-of-type{padding-left:24px;}&:last-of-type{padding-right:24px;}}th{margin-top:auto;}td{padding-top:1.6rem;padding-bottom:1.6rem;}}}tbody{box-sizing:border-box;}"], function (_ref) {
  var $customNumCols = _ref.$customNumCols;
  return css(["grid-template-columns:", ";"], $customNumCols ? "repeat(".concat($customNumCols, ", 1fr)") : "repeat(3, 1fr)");
});
/* Borders are on td elements to avoid doubling the width */

var BodyRow = /*#__PURE__*/styled.tr.withConfig({
  displayName: "TableRadioInput__BodyRow",
  componentId: "sc-voepmi-1"
})(["position:relative;*{user-select:none;}&:first-of-type{border-top-left-radius:0.8rem;border-top-right-radius:0.8rem;}&:last-of-type{border-bottom-left-radius:0.8rem;border-bottom-right-radius:0.8rem;td{border-bottom:1px solid ", ";}}&:focus-within{transition:color ", ",border ", ",background ", ";", ";}label{box-sizing:border-box;cursor:pointer;&:after{content:'';", ";}}span{transition:color ", ";position:relative;z-index:1;pointer-events:none;}", ";", ";"], COLORS.NEUTRAL_300, TRANSITION_DEFAULT, TRANSITION_DEFAULT, TRANSITION_DEFAULT, function (_ref2) {
  var $focus = _ref2.$focus;
  return $focus && css(["", ";td{border-color:", ";}+ tr td{border-top-color:", ";}"], focusStyles, COLORS.PRIMARY_500, COLORS.PRIMARY_500);
}, positionAbsolute('-1px', '-1px', '-1px', '-1px'), TRANSITION_DEFAULT, function (_ref3) {
  var $active = _ref3.$active;
  return $active && css(["background:", ";color:", ";", ";&& td{border-color:", ";}span,label{color:", ";}"], COLORS.PRIMARY_500, COLORS.WHITE, highContrastOutline, COLORS.PRIMARY_500, COLORS.WHITE);
}, function (_ref4) {
  var $disabled = _ref4.$disabled;
  return $disabled ? css(["background:", ";border-color:", ";color:", ";pointer-events:none;span,label{color:", ";", ";}"], COLORS.NEUTRAL_200, COLORS.NEUTRAL_300, COLORS.NEUTRAL_400, COLORS.NEUTRAL_400, highContrast(highContrastGrayColor)) : css(["", ";", ";"], hoverChild('td', 'border-color', COLORS.NEUTRAL_300, COLORS.PRIMARY_500), hoverChild('+ tr td', 'border-top-color', COLORS.NEUTRAL_300, COLORS.PRIMARY_500));
});
/* Borders are on td elements to avoid doubling the width */

var Td = /*#__PURE__*/styled.td.withConfig({
  displayName: "TableRadioInput__Td",
  componentId: "sc-voepmi-2"
})(["", ";border-top:1px solid ", ";&:first-child{border-left:1px solid ", ";border-top-left-radius:inherit;border-bottom-left-radius:inherit;}&:last-child{border-right:1px solid ", ";border-top-right-radius:inherit;border-bottom-right-radius:inherit;}input{width:0;height:0;}"], function (_ref5) {
  var $colSpan = _ref5.$colSpan;
  return $colSpan && gridColSpan($colSpan);
}, COLORS.NEUTRAL_300, COLORS.NEUTRAL_300, COLORS.NEUTRAL_300);

var TableRadioRow = function TableRadioRow(_ref6) {
  var row = _ref6.row,
      name = _ref6.name,
      onChange = _ref6.onChange,
      id = _ref6.id,
      rest = _objectWithoutProperties(_ref6, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focus = _useState2[0],
      setFocus = _useState2[1];

  var _useExtendedField = useExtendedField({
    name: name,
    type: 'radio',
    value: row.value
  }, {
    onChange: onChange
  }),
      _useExtendedField2 = _slicedToArray(_useExtendedField, 2),
      _useExtendedField2$ = _useExtendedField2[0],
      _onBlur = _useExtendedField2$.onBlur,
      formikInputConfig = _objectWithoutProperties(_useExtendedField2$, _excluded2),
      formikValue = _useExtendedField2[1].value;

  return /*#__PURE__*/React.createElement(BodyRow, _extends({
    $focus: focus,
    $active: "".concat(formikValue) === "".concat(row.value),
    $disabled: row.disabled,
    "aria-disabled": row.disabled === true ? true : undefined
  }, rest), row.display.map(function (cell, cellIndex) {
    var showDisabledMessage = row.disabled && row.disabledMessage;
    if (showDisabledMessage && cellIndex > 1) return null;
    return /*#__PURE__*/React.createElement(Td, {
      $colSpan: showDisabledMessage && cellIndex === 1 ? row.display.length - 1 : undefined,
      key: cellIndex,
      role: "cell"
    }, cellIndex === 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", _extends({}, formikInputConfig, {
      onFocus: function onFocus(e) {
        return setFocus(isKeyboard(e));
      },
      onBlur: function onBlur(e) {
        _onBlur(e);

        setFocus(false);
      },
      disabled: row.disabled,
      id: id,
      "data-test-id": id
    })), /*#__PURE__*/React.createElement("label", {
      className: TYPOGRAPHY_CLASSNAMES.INPUT_SEMIBOLD,
      htmlFor: id
    }, cell)) : /*#__PURE__*/React.createElement("span", {
      className: TYPOGRAPHY_CLASSNAMES.INPUT_SEMIBOLD
    }, showDisabledMessage ? row.disabledMessage : cell));
  }));
};

var TableRadioInput = function TableRadioInput(_ref7) {
  var name = _ref7.name,
      tableHeaders = _ref7.tableHeaders,
      values = _ref7.values,
      customNumCols = _ref7.customNumCols,
      $css = _ref7.$css,
      className = _ref7.className,
      collapseError = _ref7.collapseError,
      errorText = _ref7.errorText,
      rest = _objectWithoutProperties(_ref7, _excluded3);

  var idRef = useRef(dotToDash(name)).current;

  var _useExtendedField3 = useExtendedField({
    name: name
  }),
      _useExtendedField4 = _slicedToArray(_useExtendedField3, 2),
      error = _useExtendedField4[1].errorText;

  return /*#__PURE__*/React.createElement(Div, {
    className: className,
    $css: $css
  }, /*#__PURE__*/React.createElement(Wrapper, {
    $customNumCols: customNumCols,
    as: "table",
    role: "table"
  }, /*#__PURE__*/React.createElement("thead", {
    role: "rowgroup"
  }, /*#__PURE__*/React.createElement("tr", {
    role: "row"
  }, tableHeaders.map(function (header, i) {
    return /*#__PURE__*/React.createElement("th", {
      style: {
        display: 'flex',
        flexDirection: 'column-reverse'
      },
      role: "columnheader",
      className: TYPOGRAPHY_CLASSNAMES.S,
      key: i
    }, header);
  }))), /*#__PURE__*/React.createElement("tbody", {
    role: "rowgroup"
  }, values.map(function (row, rowIndex) {
    var key = "table-row-input-".concat(rowIndex, "-").concat(toAlphanumericDashString(row.value));
    var id = "".concat(dotToDash(name), "-").concat(rowIndex);
    return /*#__PURE__*/React.createElement(TableRadioRow, _extends({
      role: "row",
      key: key,
      id: id,
      row: row
    }, rest, {
      name: name
    }));
  }))), /*#__PURE__*/React.createElement(InputError, {
    $css: css(["margin-top:6px;"]),
    collapseError: collapseError,
    errorId: idRef,
    errorText: errorText || error
  }));
};

export default TableRadioInput;